/**
 * Lectures propres aux tableaux de bord (manuel 2, 09/10/2026).
 *
 * Les autres chiffres réutilisent les hooks (et donc les clés de cache) des
 * écrans concernés : un geste fait sur l'écran (validation, versement…) se
 * retrouve sur le tableau de bord sans rechargement.
 */
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { toIsoDay } from "@/pages/dashboard-parts/periodes";
import type { CompteReversement } from "@/pages/dashboard-parts/alertes";

export interface LiturgieDuJour {
  date: string;
  saison: { code: string | null; libelle: string } | null;
  saint: { nom: string; titre: string | null; qualite: string | null; degre: string | null } | null;
  parole: { reference: string; titre: string | null; livre: string | null } | null;
}

/**
 * Parole et saint du jour (BFF /api/micro/liturgie/jour : parish-service
 * liturgy + priere-service évangile). La date fait partie de la clé : le cache
 * (staleTime infini) ne sert jamais la liturgie de la veille.
 */
export function useLiturgieDuJour(parishId: string | null | undefined) {
  const jour = toIsoDay(new Date());
  return useQuery<LiturgieDuJour>({
    queryKey: ["liturgie-du-jour", jour, parishId ?? null],
    queryFn: async () => {
      const params = new URLSearchParams({ date: jour });
      if (parishId) params.append("parishId", parishId);
      const res = await apiRequest("GET", `/api/micro/liturgie/jour?${params}`);
      return (await res.json()) as LiturgieDuJour;
    },
  });
}

/** Rôles que finance-service laisse lire le compte de liaison avec l'Économat. */
export const ROLES_COMPTE_REVERSEMENT = ["cure", "econome", "secretaire_general"];

/**
 * Compte de la paroisse auprès de l'Économat : MÊME clé que l'écran « Mes
 * reversements » (déclarer un versement l'invalide pour les deux).
 */
export function useCompteReversement(parishId: string | null | undefined, enabled = true) {
  return useQuery<CompteReversement>({
    queryKey: [`/api/micro/reversements/compte/${parishId}`],
    enabled: !!parishId && enabled,
  });
}

export interface VersementEconomat {
  id: string;
  montant: number;
  dateVersement: string;
  statut: string;
  /** Écart constaté par l'Économat : montant reçu = montant + écart (D-H). */
  ecartConstate?: number | null;
  encaisseAt?: string | null;
  rapprocheAt?: string | null;
}

/** Versements déclarés (même clé que « Mes reversements »). */
export function useVersementsEconomat(parishId: string | null | undefined, enabled = true) {
  return useQuery<{ items: VersementEconomat[] }>({
    queryKey: [`/api/micro/reversements?parishId=${parishId}`],
    enabled: !!parishId && enabled,
  });
}
